// src/helpers/pdfPendientesProveedor.js
import { jsPDF } from "jspdf";
import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

/**
 * Genera un PDF con el resumen de remitos pendientes por proveedor
 * (lo que ves en el modal: tabla A/B/Flete/Saldo + totales).
 */
export async function generarPDFPendientesProveedor({
  proveedor,
  cliente = null,
  pendientes = [],
  totales,
  fecha,
  logoUrl = "/img/logo-sanpancracio.png", // 👈 mismo logo que remitos
  empresa = {},
}) {
  if (!pendientes.length) {
    alert("No hay remitos pendientes para este proveedor.");
    return;
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const logoDataURL = await loadImageAsDataURL(logoUrl);

  let y = 12;

  // 🔹 Encabezado EXACTAMENTE igual al de Remitos (texto + logo)
  drawHeader(doc, y, logoDataURL, empresa);
  y += 34;

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(
    "RESUMEN DE REMITOS PENDIENTES PARA ORDEN DE PAGO",
    105,
    y,
    { align: "center" }
  );
  y += 8;

  // Datos proveedor / cliente / fecha
  const provNombre = S(
    proveedor?.razonsocial || proveedor?.nombre || proveedor || "-"
  );
  const cliNombre = cliente
    ? S(cliente?.razonsocial || cliente?.nombre || cliente)
    : null;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Proveedor: ${provNombre}`, 12, y);
  if (cliNombre) {
    doc.text(`Cliente (filtro): ${cliNombre}`, 12, y + 5);
  }
  doc.text(`Fecha: ${fmtDate(fecha)}`, 198, y, { align: "right" });
  y += cliNombre ? 12 : 8;

  // Cabecera tabla
  const startX = 12;
  const colRem = 24;
  const colFecha = 24;
  const colA = 36;
  const colB = 36;
  const colF = 28;
  const colSaldo = 38;
  const totalWidth = colRem + colFecha + colA + colB + colF + colSaldo;

  const headerY = y;
  doc.setFillColor(225, 235, 255);
  doc.setDrawColor(180, 180, 200);
  doc.rect(startX, headerY - 5, totalWidth, 7, "F");

  doc.setFont("helvetica", "bold");
  doc.text("Remito", startX + 2, headerY);
  doc.text("Fecha", startX + colRem + 2, headerY);
  doc.text("Compra A", startX + colRem + colFecha + colA - 2, headerY, {
    align: "right",
  });
  doc.text("Compra B", startX + colRem + colFecha + colA + colB - 2, headerY, {
    align: "right",
  });
  doc.text("Flete", startX + colRem + colFecha + colA + colB + colF - 2, headerY, {
    align: "right",
  });
  doc.text(
    "Saldo",
    startX + colRem + colFecha + colA + colB + colF + colSaldo - 2,
    headerY,
    { align: "right" }
  );

  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const maxY = 260;

  const filasOrdenadas = [...pendientes].sort(
    (a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0)
  );

  for (const r of filasOrdenadas) {
    if (y > maxY) {
      doc.addPage();
      y = 20;
    }

    const compraA = Number(r.totalCompraA || 0);
    const compraB = Number(r.totalCompraB || 0);
    const flete   = Number(r.flete || 0);
    const pagado  = Number(r.pagadoProveedor ?? r.pagado ?? 0);
    const pactado = compraA + compraB + flete;
    const saldo   = Number((pactado - pagado).toFixed(2));

    const nroRem =
      r.codrem != null ? String(r.codrem).padStart(6, "0") : "-";

    const rowY = y;

    doc.setDrawColor(230);
    doc.line(
      startX,
      rowY + 3,
      startX + totalWidth,
      rowY + 3
    );

    doc.text(`#${nroRem}`, startX + 2, rowY + 2);
    doc.text(fmtDate(r.fecha), startX + colRem + 2, rowY + 2);

    doc.text(
      `$ ${fmtNum(compraA)}`,
      startX + colRem + colFecha + colA - 2,
      rowY + 2,
      { align: "right" }
    );
    doc.text(
      `$ ${fmtNum(compraB)}`,
      startX + colRem + colFecha + colA + colB - 2,
      rowY + 2,
      { align: "right" }
    );
    doc.text(
      `$ ${fmtNum(flete)}`,
      startX + colRem + colFecha + colA + colB + colF - 2,
      rowY + 2,
      { align: "right" }
    );
    doc.text(
      `$ ${fmtNum(saldo)}`,
      startX + colRem + colFecha + colA + colB + colF + colSaldo - 2,
      rowY + 2,
      { align: "right" }
    );

    y += 6;
  }

  // 🔹 Resumen en DOS renglones para que no se corte
  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setDrawColor(120, 120, 160);
  doc.setFillColor(220, 230, 255);

  const resumenY = y;
  const resumenHeight = 12; // un poco más alto para dos líneas
  doc.rect(startX, resumenY - 6, totalWidth, resumenHeight, "F");

  // Línea 1: A y B
  doc.text(
    `£ A: $ ${fmtNum(totales?.compraA)}   —   £ B: $ ${fmtNum(
      totales?.compraB
    )}`,
    startX + 2,
    resumenY - 1
  );

  // Línea 2: Flete y Saldo
  doc.text(
    `£ Flete: $ ${fmtNum(totales?.flete)}   —   £ Saldo: $ ${fmtNum(
      totales?.saldo
    )}`,
    startX + 2,
    resumenY + 4
  );

  // Pie
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.text(
    "Documento electrónico generado por el sistema San Pancracio — Resumen para Orden de Pago",
    12,
    292
  );

  const safeProv = provNombre.replace(/[^\w\-]+/g, "_");
  const filename = `Pendientes_${safeProv || "proveedor"}.pdf`;
  doc.save(filename);
}
